// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import AnalysisCard from '../components/AnalysisCard';
import AnnotationPanel from '../components/AnnotationPanel';
import RetourAvisPanel from '../components/RetourAvisPanel';
import { Button } from '../components/ui/button';
import { annulerAnalyse, getProjects } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';

const PAGE_SIZE = 20;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.normascan.fr';

const Analyses = () => {
  const [analyses, setAnalyses]                     = useState([]);
  const [loading, setLoading]                       = useState(true);
  const [loadingMore, setLoadingMore]               = useState(false);
  const [error, setError]                           = useState(null);
  const [hasMore, setHasMore]                       = useState(false);
  const [total, setTotal]                           = useState(0);
  const [offset, setOffset]                         = useState(0);
  const [searchInput, setSearchInput]               = useState('');
  const [activeSearch, setActiveSearch]             = useState('');
  const [annotationAnalysis, setAnnotationAnalysis] = useState(null);
  // Boucle de retour « cet avis est faux » (S3). ⚠️ État SÉPARÉ de celui du
  // panneau d'annotation : les deux n'ont ni le même droit d'accès ni le même
  // objet — les annotations sont un outil interne, le retour est ouvert à tous.
  const [retourAnalysis, setRetourAnalysis]         = useState(null);
  const [location, setLocation]                     = useLocation();
  const { user } = useAuth();
  const { t } = useI18n();
  const LS_ANNOT_KEY = 'normascan_annotation_open';
  const canAnnotate = ['admin', 'dev', 'partner'].includes(user?.role) || !!user?.lic_annotations_enabled;
  const searchRef = useRef(null);

  // Filtres serveur
  const [filterStatus, setFilterStatus]         = useState('');
  const [filterProjectId, setFilterProjectId]   = useState('');
  const [filterUserSearch, setFilterUserSearch] = useState('');
  // Filtres client
  const [filterResult, setFilterResult]         = useState('');
  const [filterDateFrom, setFilterDateFrom]     = useState('');
  const [filterDateTo, setFilterDateTo]         = useState('');
  const [projectsList, setProjectsList]         = useState([]);
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  // Vue équipe (15/09/2026) — administrateur d'une licence Cabinet ou Entreprise.
  // '' = mes analyses (défaut), 'equipe' = tous les sièges, sinon l'id d'un membre.
  // Une liste FERMÉE des membres de SA licence, et non un champ libre : il ne
  // peut chercher que parmi ses collègues. Le serveur borne de toute façon.
  const estAdminLicence = !!user?.license && user?.license_role === 'license_admin';
  const [vueEquipe, setVueEquipe]               = useState('');
  const [membresLicence, setMembresLicence]     = useState([]);

  // Lire search depuis query param (vient de la sidebar)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('search') || '';
    if (q) { setSearchInput(q); setActiveSearch(q); }
  }, []);

  // Restaurer panel annotation
  useEffect(() => {
    if (!canAnnotate) return;
    const saved = sessionStorage.getItem(LS_ANNOT_KEY);
    if (saved) { try { setAnnotationAnalysis(JSON.parse(saved)); } catch (_) {} }
  }, [canAnnotate]);

  const loadAnalyses = useCallback(async (search = '', off = 0, append = false, filters = {}) => {
    try {
      if (append) setLoadingMore(true);
      else { setLoading(true); setError(null); }

      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: off });
      if (search) params.set('search', search);
      if (filters.status)      params.set('status', filters.status);
      if (filters.project_id)  params.set('project_id', filters.project_id);
      if (filters.user_search) params.set('user_search', filters.user_search);
      if (filters.vue === 'equipe') params.set('vue', 'equipe');
      else if (filters.vue)        params.set('membre', filters.vue);

      const response = await fetch(`${API_BASE_URL}/api/analyses?${params}`);
      if (!response.ok) throw new Error('Erreur chargement');
      const data = await response.json();

      setAnalyses(prev => append ? [...prev, ...(data.analyses || [])] : (data.analyses || []));
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      setOffset(off + PAGE_SIZE);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const filtresServeur = { status: filterStatus, project_id: filterProjectId, user_search: debouncedUserSearch, vue: vueEquipe };

  useEffect(() => {
    loadAnalyses(activeSearch, 0, false, filtresServeur);
  }, [activeSearch, filterStatus, filterProjectId, debouncedUserSearch, vueEquipe, loadAnalyses]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!estAdminLicence) return;
    fetch(`${API_BASE_URL}/api/license/users`)
      .then(r => (r.ok ? r.json() : { users: [] }))
      .then(d => setMembresLicence((d.users || []).filter(u => u.id !== user?.id)))
      .catch(() => {});
  }, [estAdminLicence, user?.id]);

  useEffect(() => {
    getProjects().then(data => {
      setProjectsList(Array.isArray(data) ? data : (data?.projects || []));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(filterUserSearch), 600);
    return () => clearTimeout(timer);
  }, [filterUserSearch]);

  // Sync panel
  useEffect(() => {
    if (!annotationAnalysis || analyses.length === 0) return;
    const fresh = analyses.find(a => (a.id || a.analysis_id) === (annotationAnalysis.id || annotationAnalysis.analysis_id));
    if (!fresh || fresh.status !== 'COMPLETED') handleCloseAnnotation();
  }, [analyses]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchInput.trim());
    setOffset(0);
  };

  const handleClearSearch = () => { setSearchInput(''); setActiveSearch(''); setOffset(0); };

  const handleLoadMore = () => loadAnalyses(activeSearch, offset, true, filtresServeur);

  const handleAnnotate = useCallback((analysis) => {
    setAnnotationAnalysis(analysis);
    sessionStorage.setItem(LS_ANNOT_KEY, JSON.stringify(analysis));
  }, []);

  const handleCloseAnnotation = useCallback(() => {
    setAnnotationAnalysis(null);
    sessionStorage.removeItem(LS_ANNOT_KEY);
  }, []);

  const handleDelete = async (analysisId) => {
    if (!window.confirm(t('confirm_delete_analysis'))) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        const pending = sessionStorage.getItem('normascan_pending_upload');
        if (pending) {
          try { const p = JSON.parse(pending); if (p.analysis_id === analysisId) sessionStorage.removeItem('normascan_pending_upload'); } catch (_) {}
        }
        window.dispatchEvent(new CustomEvent('analysis_deleted', { detail: { analysisId } }));
        loadAnalyses(activeSearch, 0, false, filtresServeur);
      } else {
        const data = await response.json();
        alert(`${t('err_prefix')}${data.error}`);
      }
    } catch (err) { alert(`${t('err_prefix')}${err.message}`); }
  };

  // Annuler une analyse EN COURS depuis sa carte (01/10/2026). Le serveur la
  // passe en CANCELLED et l'arrête ; la liste masque les annulées, la carte
  // disparaît donc au rechargement. L'onglet qui la suivait éventuellement
  // (Dashboard) en est prévenu par SocketIO, ou s'en aperçoit à sa reprise.
  const handleCancel = async (analysisId) => {
    if (!window.confirm(t('confirm_cancel_analysis'))) return;
    try {
      await annulerAnalyse(analysisId);
      loadAnalyses(activeSearch, 0, false, filtresServeur);
    } catch (err) { alert(`${t('err_prefix')}${err.message}`); }
  };

  // Position rendue par le rapport, déduite des COMPTEURS D'AVIS et non d'un
  // niveau global. L'ancien filtre lisait `conformity_level` et le comparait à
  // EXCELLENT / BON / ACCEPTABLE / INSUFFISANT — un enum qui n'existe plus :
  // le filtre ne rendait donc plus jamais 'conforme' ni 'reserve'.
  //
  // Depuis le 14/08/2026 le back agrège de la même façon et `conformity_level`
  // vaut FAVORABLE / RESERVE / DEFAVORABLE : les deux disent enfin la même
  // chose. On continue de lire les compteurs — ils sont posés pour toutes les
  // analyses, y compris celles d'avant la bascule.
  //
  // Les compteurs sont posés par flask_app.py (pass_count / warning_count /
  // fail_count → nb_conformes / nb_reserves / nb_non_conformes) et disent
  // directement les trois positions : un seul avis défavorable suffit à ce que
  // le rapport en porte un.
  const getConformityResult = (analysis) => {
    let meta = {};
    try { const m = analysis.metadata; meta = m ? (typeof m === 'string' ? JSON.parse(m) : m) : {}; } catch(_) {}
    const defavorables = Number(meta.nb_non_conformes) || 0;
    const reserves     = Number(meta.nb_reserves)      || 0;
    const favorables   = Number(meta.nb_conformes)     || 0;
    if (defavorables > 0) return 'nc';
    if (reserves > 0)     return 'reserve';
    if (favorables > 0)   return 'conforme';
    return '';  // aucune règle évaluée : ni favorable, ni réserve, ni défavorable
  };

  const filteredAnalyses = analyses.filter(a => {
    if (filterResult && getConformityResult(a) !== filterResult) return false;
    if (filterDateFrom) {
      const d = new Date(a.upload_date || a.created_at);
      if (isNaN(d) || d < new Date(filterDateFrom)) return false;
    }
    if (filterDateTo) {
      const d = new Date(a.upload_date || a.created_at);
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      if (isNaN(d) || d > to) return false;
    }
    return true;
  });

  const hasActiveFilters = !!(filterStatus || filterResult || filterProjectId || filterDateFrom || filterDateTo || filterUserSearch);
  const clearAllFilters = () => {
    setFilterStatus('');
    setFilterResult('');
    setFilterProjectId('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterUserSearch('');
  };

  const selectStyle = {
    fontSize: 12, padding: '6px 8px', borderRadius: 6,
    border: '1px solid var(--gray)', background: 'var(--bg-input)',
    color: 'var(--black)', cursor: 'pointer', outline: 'none',
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <main className="container mx-auto px-4 py-8 max-w-6xl flex-1">

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--black)', marginBottom: 4 }}>
              📂 {t('nav_analyses')}
            </h1>
            {!loading && !error && (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {total} {total > 1 ? t('analyses_label_pl') : t('analyses_label')}{activeSearch && ` ${t('analyses_for')} "${activeSearch}"`}
              </p>
            )}
          </div>
          <Button onClick={() => loadAnalyses(activeSearch, 0, false)} variant="outline" size="sm">{t('btn_refresh')}</Button>
        </div>

        {/* Barre de recherche */}
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray)', background: 'var(--bg-input)' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>🔍</span>
            <input
              ref={searchRef}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder={t('search_placeholder')}
              style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, color: 'var(--black)', outline: 'none' }}
            />
            {searchInput && (
              <button type="button" onClick={handleClearSearch} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13 }}>✕</button>
            )}
          </div>
          <Button type="submit" size="sm">{t('filter_label')}</Button>
        </form>

        {/* Barre de filtres */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {t('analyses_filters_title')} :
          </span>
          {estAdminLicence && (
            <select value={vueEquipe} onChange={e => setVueEquipe(e.target.value)} style={selectStyle}>
              <option value="">{t('vue_mes_analyses')}</option>
              <option value="equipe">{t('vue_toute_equipe')}</option>
              {membresLicence.map(m => <option key={m.id} value={m.id}>{m.email}</option>)}
            </select>
          )}
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">{t('filter_status_all')}</option>
            <option value="COMPLETED">{t('status_completed')}</option>
            <option value="PROCESSING">{t('status_processing')}</option>
            <option value="PENDING">{t('status_pending')}</option>
            <option value="ERROR">{t('status_error')}</option>
          </select>
          <select value={filterResult} onChange={e => setFilterResult(e.target.value)} style={selectStyle}>
            <option value="">{t('filter_result_all')}</option>
            <option value="conforme">{t('filter_result_conforme')}</option>
            <option value="reserve">{t('filter_result_reserve')}</option>
            <option value="nc">{t('filter_result_nc')}</option>
          </select>
          {projectsList.length > 0 && (
            <select value={filterProjectId} onChange={e => setFilterProjectId(e.target.value)} style={selectStyle}>
              <option value="">{t('filter_project_all')}</option>
              {projectsList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('filter_date_from_label')}</span>
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} style={{ ...selectStyle, padding: '5px 8px' }} />
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('filter_date_to_label')}</span>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} style={{ ...selectStyle, padding: '5px 8px' }} />
          </div>
          {['admin', 'dev'].includes(user?.role) && (
            <input
              type="text"
              value={filterUserSearch}
              onChange={e => setFilterUserSearch(e.target.value)}
              placeholder={t('filter_user_ph')}
              style={{ ...selectStyle, minWidth: 160 }}
            />
          )}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: 6 }}
            >
              ✕ {t('filter_active')}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">⏳</div>
            <p style={{ color: 'var(--text-muted)' }}>{t('loading_label')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">❌</div>
            <p className="text-red-600 mb-4 font-semibold">Erreur: {error}</p>
            <Button onClick={() => loadAnalyses(activeSearch, 0, false)}>{t('retry_label')}</Button>
          </div>
        ) : filteredAnalyses.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">{activeSearch || hasActiveFilters ? '🔍' : '📂'}</div>
            <p className="mb-4" style={{ color: 'var(--text-muted)' }}>
              {activeSearch ? `${t('no_results_search')} "${activeSearch}"` : t('no_analyses')}
            </p>
            {hasActiveFilters
              ? <Button onClick={clearAllFilters} variant="outline">{t('clear_search_label')}</Button>
              : activeSearch
                ? <Button onClick={handleClearSearch} variant="outline">{t('clear_search_label')}</Button>
                : <Link href="/dashboard"><Button size="lg">{t('create_analysis_cta')}</Button></Link>
            }
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAnalyses.map((analysis) => (
                <AnalysisCard
                  key={analysis.id || analysis.analysis_id}
                  analysis={analysis}
                  onDelete={handleDelete}
                  onCancel={handleCancel}
                  onAnnotate={canAnnotate ? handleAnnotate : undefined}
                  // ⚠️ Sans garde de droit, contrairement à `onAnnotate` : tout
                  // compte authentifié peut signaler, sur ses propres analyses
                  // (décision de Marc, 25/08/2026). Mesuré le même jour, le canal
                  // des annotations — verrouillé, lui — avait produit ZÉRO ligne.
                  // Reproduire ce verrou ici reproduirait le goulot que ce
                  // chantier existe pour lever.
                  onSignaler={setRetourAnalysis}
                  isAdmin={user?.role === 'admin'}
                  // L'analyse d'un collègue, en vue équipe : consultation seule.
                  // Les rôles Evalens gardent leurs actions habituelles.
                  lectureSeule={!['admin', 'dev'].includes(user?.role)
                    && !!analysis.user_id && analysis.user_id !== user?.id}
                />
              ))}
            </div>

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Button onClick={handleLoadMore} variant="outline" disabled={loadingMore}>
                  {loadingMore ? t('loading_more') : t('load_more')}
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {annotationAnalysis && canAnnotate && (
        <AnnotationPanel analysis={annotationAnalysis} onClose={handleCloseAnnotation} />
      )}

      {retourAnalysis && (
        <RetourAvisPanel analysis={retourAnalysis} onClose={() => setRetourAnalysis(null)} />
      )}
    </div>
  );
};

export default Analyses;